import { useMemo, useCallback, useEffect, useRef } from "react";
import { Stack } from "@fluentui/react";
import DOMPurify from "dompurify";
import { marked } from "marked";
import { SectionPage } from "@snsw-gel/react";
import { FollowupQuestions } from "../FollowupQuestions";
import { OtherLinks } from "../OtherLinks";
import type { TurnFollowup, TurnStream } from "../chatTurn";
import SnswAvatar from "../../assets/snswAvatar";
import styles from "../Answer/Answer.module.css";

marked.setOptions({ breaks: true, gfm: true });

interface StreamingAnswerProps {
  state: TurnStream;
  onCitationClicked: (filePath: string, citationText?: string) => void;
  onFollowupClick: (question: TurnFollowup) => void;
}

export const StreamingAnswer = ({ state, onCitationClicked, onFollowupClick }: StreamingAnswerProps) => {
  const { content, citations, otherLinks, followupQuestions, error, isStreaming } = state;
  const answerTextRef = useRef<HTMLDivElement>(null);

  const getProcessedHtml = useCallback((text: string): string => {
    const html = marked.parse(text) as string;
    return DOMPurify.sanitize(html, {
      ADD_ATTR: ["data-citation-index", "data-citation-text"]
    });
  }, []);

  const html = useMemo(() => getProcessedHtml(content), [content, getProcessedHtml]);

  const handleFollowupClick = useCallback(
    (question: TurnFollowup) => {
      onFollowupClick(question);
    },
    [onFollowupClick]
  );

  useEffect(() => {
    const answerTextEl = answerTextRef.current;
    if (!answerTextEl) return;

    const supContainers = answerTextEl.querySelectorAll(".supContainer");
    if (supContainers.length === 0) return;

    const handleClick = (event: Event) => {
      event.preventDefault();
      const target = event.currentTarget as HTMLElement;
      const citationIndexAttr = target.getAttribute("data-citation-index");
      const citationText = target.getAttribute("data-citation-text");

      if (citationIndexAttr) {
        const citationIndex = parseInt(citationIndexAttr, 10);
        const citation = citations.find(c => c.index === citationIndex);
        if (citation) {
          const decodedText = citationText ? decodeURIComponent(citationText) : undefined;
          onCitationClicked(citation.url, decodedText);
        }
      }
    };

    supContainers.forEach(container => {
      container.addEventListener("click", handleClick);
    });

    return () => {
      supContainers.forEach(container => {
        container.removeEventListener("click", handleClick);
      });
    };
  }, [html, citations, onCitationClicked]);

  const renderContent = () => {
    if (error) {
      return (
        <div className={styles.errorContainer}>
          <p className={styles.errorText}>Error: {error.error}</p>
          {error.recoverable && <p className={styles.errorHint}>You can try again.</p>}
        </div>
      );
    }

    if (!content) {
      return (
        <div className={styles.answerText}>
          Generating answer
          <span className={styles.loadingdots} />
        </div>
      );
    }

    return <div ref={answerTextRef} className={styles.answerText} dangerouslySetInnerHTML={{ __html: html }} />;
  };

  return (
    <SectionPage className={styles.answerContainer}>
      <Stack>
        <SectionPage className={styles.answerRoleChatGPT}>
          <SnswAvatar />
          <h4 className={styles.answerGPTTitle}>Sage</h4>
        </SectionPage>
      </Stack>

      <Stack.Item>{renderContent()}</Stack.Item>

      {!isStreaming && followupQuestions.length > 0 && <FollowupQuestions questions={followupQuestions} onQuestionClick={handleFollowupClick} />}

      {!isStreaming && otherLinks.length > 0 && <OtherLinks otherLinks={otherLinks} onCitationClicked={onCitationClicked} />}

      {!isStreaming && error && (
        <div className={styles.errorActions}>
          {error.recoverable && (
            <button type="button" className={styles.retryButton} onClick={() => window.location.reload()}>
              Try Again
            </button>
          )}
        </div>
      )}
    </SectionPage>
  );
};
